import { ClipboardCheck } from 'lucide-react';
import type { TimedPaper } from './examPlacement';

/** What the card prints: a paper as the teacher's and the family's reads both carry it. */
export interface ExamSlotPaper extends TimedPaper {
  /** "Unit Test I" — what the institute called the paper. */
  name: string;
  subject: { name: string };
}

interface ExamSlotCardProps {
  exam: ExamSlotPaper;
  /**
   * The class sitting it. The teacher's week spans several classes, so it names
   * one; a child's or parent's grid is already that one class, and leaves it out.
   */
  sectionName?: string;
}

/**
 * One paper in a period of a timetable grid — the teacher's week, the child's
 * and the parent's all draw it with this card, so an exam reads the same on
 * every panel.
 *
 * Drawn in the same shape as a lesson so the grid reads as one thing, but in
 * amber — the colour a scheduled paper already wears on the Exams page — and
 * not clickable: a paper is the office's to move, not the reader's.
 */
export function ExamSlotCard({ exam, sectionName }: ExamSlotCardProps) {
  const timing = exam.startTime ? `${exam.startTime}${exam.endTime ? ` – ${exam.endTime}` : ''}` : null;

  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 p-2.5 select-none print:gap-1.5 print:rounded-lg print:p-1.5">
      <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber print:mt-0 print:h-3.5 print:w-3.5" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-[10px] font-bold uppercase tracking-wider text-amber print:text-[8px]">Exam</span>
        <span className="truncate text-sm font-bold leading-tight text-ink print:text-[11px]">
          {exam.subject.name}
        </span>
        <span className="mt-0.5 truncate text-[11px] font-medium text-ink-3 print:mt-0 print:text-[9px]">
          {sectionName ? `${sectionName} · ${exam.name}` : exam.name}
        </span>
        {timing && (
          <span className="mt-0.5 text-[11px] font-medium text-ink-3 tabular-nums print:mt-0 print:text-[9px]">
            {timing}
          </span>
        )}
      </div>
    </div>
  );
}
